import { useLocale, useTranslations } from 'next-intl'
import PdSection from '../pd-section/pd-section'
import PdImage from '../pd-image/pd-image'
import Link from 'next/link'
import AngleRight from '../pd-angle-right/pd-angle-right'
import { useMemo } from 'react'
import clsx from 'clsx'
import { useRegionDetector } from '@/common/region-locale'

interface CellProps {
  label: string
  value: string
}

function Cell({ label, value }: CellProps) {
  const locale = useLocale()
  const isSmallLanguage = useRegionDetector(['japan', 'spain', 'france'])

  const fontSize = useMemo(() => {
    if (isSmallLanguage) {
      return 'laptop:text-32'
    }
    return 'laptop:text-5xl'
  }, [locale])

  return (
    <div className="inline-flex flex-col gap-4 px-3 py-2 laptop:px-6 laptop:py-4 bg-pdbg">
      <h4 className={clsx('text-pdblue text-32 font-bold', fontSize)}>{value}</h4>
      <span className="text-black/60 text-sm laptop:font-semibold font-normal">{label}</span>
    </div>
  )
}

interface AboutUsSectionProps {
  title: string
  mediaUrl: string
  imageAlt?: string
  desc: string
  indicatorData: CellProps[]
}

function AboutUsSection({ title, mediaUrl, imageAlt, desc, indicatorData }: AboutUsSectionProps) {
  const t = useTranslations()
  const data = indicatorData
  const imgUrl = mediaUrl || 'https://cdn.pudutech.com/official-website/about-us/about-us-bg.jpg'
  return (
    <PdSection title={title} bgWhite>
      <div className="flex laptop:flex-row flex-col max-w-full w-300 laptop:h-165 laptop:gap-12 gap-6 px-6 laptop:px-0">
        <div className="relative laptop:w-1/2 w-full h-327px laptop:h-full shrink">
          <PdImage width="100%" src={imgUrl} alt={mediaUrl ? imageAlt : undefined} />
        </div>
        <div className="laptop:w-1/2 w-full laptop:h-full shrink flex flex-col justify-between">
          <div className="grid grid-cols-2 gap-2">
            {data.map((item) => (
              <Cell key={item.label} label={item.label} value={item.value} />
            ))}
          </div>

          <div className="inline-flex flex-col gap-1 mt-12 laptop:mt-0">
            <p className="ext-black/90 text-base font-normal" dangerouslySetInnerHTML={{ __html: desc }}></p>
            <Link href={'/company'}>
              <span className="laptop:text-base text-sm flex items-center text-pdblue cursor-pointer">
                {t('KnowMore')}
                <AngleRight theme="PUDUBLUE" size="4x8" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </PdSection>
  )
}

export default AboutUsSection
